import { useContext, useMemo, useState } from "react";
import "../Components/SelectionMenu/SelectionMenu.scss";
import { useRouteMatch, Link } from "react-router-dom";
import { Spring, animated } from "react-spring/renderprops";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../General/AppPreferencesProvider";
import { Label, LabelVariant, useIcon } from "@SeemplicityDev/shared";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ChatStreamContext } from "../SeemChat/Provider/ChatStreamProvider";
import {
  AI_INSIGHTS_FEED_PATH,
  AI_INSIGHTS_FEED_SLUG,
} from "../CustomizedDashboard/dashboardUrl";
import useRemediationItemsSplit from "../General/useRemediationItemsSplit";
import { useFindingEntity } from "../Findings/findingEntity";

interface SubMenuItem {
  pageLink: string;
  title: string;
  value: string;
  isBeta?: boolean;
}

interface MenuItem {
  label: string;
  value: string;
  isDisabled: boolean;
  link?: string;
  rightIcon?: string;
  leftIcon?: string;
  leftImg?: string;
  additionalMatches?: string[];
  subMenu?: SubMenuItem[];
  isAI?: boolean;
}

const AppMenu: React.FC = () => {
  const [selectedItemOpen, setSelectedItemOpen] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { getSvg } = useIcon();

  const { hideAggregationRulesPage } = useFlags();
  const isRemediationItemsSplit = useRemediationItemsSplit();
  const { entityLabel, buildRemediationItemHref } = useFindingEntity();

  const {
    appPreferences: { useAI },
    isAllowed,
  } = useContext(AppPreferencesContext);

  const {
    streamState: { isStreaming },
  } = useContext(ChatStreamContext);

  let match = useRouteMatch<{ item: string }>("/:item");
  const isAiInsightsFeed = !!useRouteMatch(AI_INSIGHTS_FEED_PATH);

  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    items.push({
      label: "Dashboards",
      value: "dashboard",
      isDisabled: false,
      ...(useAI
        ? {
            rightIcon: "seem-icon-chevron-down",
            subMenu: [
              {
                pageLink: "dashboard/",
                title: "Dashboard",
                value: "dashboard",
              },
              {
                pageLink: `dashboard/${AI_INSIGHTS_FEED_SLUG}`,
                title: "AI Insights",
                value: AI_INSIGHTS_FEED_SLUG,
              },
            ],
          }
        : { link: "dashboard/" }),
    });

    items.push({
      label: "Discovery",
      value: "discovery",
      rightIcon: "seem-icon-chevron-down",
      isDisabled: false,
      subMenu: [
        {
          pageLink: "discovery/findings-validation",
          title: "Findings Validation",
          value: "findings-validation",
        },
        {
          pageLink: "resources/list",
          title: "Resources",
          value: "resources",
        },
        // {
        //   pageLink: "resources/tags-explorer",
        //   title: "Tags Explorer",
        //   value: "tags-explorer",
        // },
      ],
    });

    const remediationItemsSegment = buildRemediationItemHref().slice(1);

    items.push({
      label: entityLabel,
      value: remediationItemsSegment,
      ...(isRemediationItemsSplit ? { additionalMatches: ["findings"] } : {}),
      isDisabled: false,
      link: remediationItemsSegment,
    });

    items.push({
      label: "Automations",
      value: "remediation",
      additionalMatches: ["remediationQueue"],
      isDisabled: false,
      link: "remediation",
    });

    if (isAllowed(userActionsEnum.VIEW_RULES_PAGE)) {
      items.push({
        label: "Policies",
        value: "policies",
        isDisabled: false,
        rightIcon: "seem-icon-chevron-down",
        subMenu: [
          {
            pageLink: "policies/findings-rules/",
            title: "Findings Rules",
            value: "findings-rules",
          },
          ...(!hideAggregationRulesPage
            ? [
                {
                  pageLink: "policies/aggregation-rules",
                  title: "Aggregation Rules",
                  value: "aggregation-rules",
                },
              ]
            : []),
          {
            pageLink: "policies/ticket-sla-rules",
            title: "Ticket SLA Rules",
            value: "ticket-sla-rules",
          },
        ],
      });
    }

    if (useAI && isAllowed(userActionsEnum.VIEW_SEEM_CHAT)) {
      // todo: add user action for this
      items.push({
        label: "AI Analysts",
        value: "analyst-runs",
        isDisabled: false,
        link: "analyst-runs",
      });
    }

    if (useAI) {
      items.push({
        label: "Seema",
        value: "seema",
        isDisabled: false,
        link: `seema/`,
        isAI: true,
      });
    }

    return items;
  }, [
    isAllowed,
    hideAggregationRulesPage,
    useAI,
    isRemediationItemsSplit,
    entityLabel,
    buildRemediationItemHref,
  ]);

  return (
    <div className={`selection-menu-wrapper primary-menu`}>
      <ul
        className={`selection-menu-list selection-menu-list-selected-${selectedIndex}`}
      >
        <div className="selection-bg" />
        {menuItems.map((menuItem, index) => {
          const currentItem = match?.params?.item;
          const activeSubItem = isAiInsightsFeed
            ? AI_INSIGHTS_FEED_SLUG
            : currentItem;
          const isActive =
            menuItem.value === currentItem ||
            menuItem?.additionalMatches?.includes(currentItem as string) ||
            menuItem?.subMenu?.some((subItem) => subItem.value === currentItem);
          const isDisabled = menuItem.isDisabled;
          const isMenuItemStreaming = menuItem.isAI && isStreaming;
          const className = `selection-menu-option no-link ${menuItem.link ? "" : "cursor-default"} ${
            isActive ? "is-selected" : ""
          } ${isDisabled ? "is-disabled" : ""} ${menuItem.isAI ? "is-ai" : ""} ${isMenuItemStreaming ? "is-streaming" : ""}`;
          const sharedProps = {
            className,
            onClick: () => setSelectedIndex(index),
            onMouseOver: () => setSelectedItemOpen(menuItem.value),
            onMouseLeave: () => setSelectedItemOpen(null),
          };

          const menuContent = (
            <li>
              {!!menuItem.isAI && (
                <img
                  src={getSvg("icon-ai")}
                  alt={isMenuItemStreaming ? "Seema is responding" : "Seema"}
                  className="seem-ai-icon"
                />
              )}
              {!!menuItem.leftIcon && (
                <i className={`seem-icon ${menuItem.leftIcon}`} />
              )}
              {!!menuItem.leftImg && (
                <div
                  style={{ backgroundImage: `url(${menuItem.leftImg})` }}
                  className={`selection-img`}
                />
              )}
              <span
                className="selection-menu-option-text"
                data-testid={`selection-menu-${menuItem.label}`}
              >
                {menuItem.label}
              </span>
              {!!menuItem.rightIcon && (
                <span className="margin-left-20">
                  <i className={`seem-icon ${menuItem.rightIcon} chevron`} />
                </span>
              )}
              <div className="menu-underline" />
              {menuItem.hasOwnProperty("subMenu") &&
                selectedItemOpen === menuItem.value && (
                  <Spring
                    native
                    // `unique` is a real react-spring renderprops runtime prop
                    // missing from this version's typings; suppress the gap.
                    // @ts-expect-error react-spring SpringProps omits `unique`
                    unique
                    from={{ opacity: 0 }}
                    to={{ opacity: 1 }}
                  >
                    {(props) => (
                      <animated.div
                        style={props}
                        className="sub-header-menu-item-wrapper"
                      >
                        {menuItem.subMenu?.map((subMenuItem) => (
                          <Link
                            key={subMenuItem.value}
                            to={`/${subMenuItem.pageLink}`}
                            className={`sub-header-menu-item-link${
                              subMenuItem.value === activeSubItem
                                ? "-active"
                                : ""
                            }`}
                            data-testid={`sub-menu-item-${subMenuItem.value}`}
                          >
                            <div className="sub-menu-item-title">
                              {subMenuItem.title}{" "}
                            </div>
                            {subMenuItem.isBeta && (
                              <Label
                                className="beta-indicator"
                                text="Beta"
                                variant={LabelVariant.Outlined}
                              />
                            )}
                          </Link>
                        ))}
                      </animated.div>
                    )}
                  </Spring>
                )}
            </li>
          );

          return menuItem.link ? (
            <Link key={index} to={`/${menuItem.link}`} {...sharedProps}>
              {menuContent}
            </Link>
          ) : (
            <div key={index} {...sharedProps}>
              {menuContent}
            </div>
          );
        })}
      </ul>
    </div>
  );
};

export default AppMenu;
